import { __decorate } from "tslib";
import { action, computed, makeObservable, observable, reaction } from "mobx";
import { LogEventName } from "../../lib/logger-types";
import { logToolbarEvent } from "../tiles/log/log-toolbar-event";
import { isCommentItem } from "./read-aloud-queue-items";
const kMaxChunkLength = 200;
export class ReadAloudService {
    constructor(stores) {
        Object.defineProperty(this, "state", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: "idle"
        });
        Object.defineProperty(this, "activePane", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "currentItem", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "queue", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "pendingCommentId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "isSupported", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "stores", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "toolbarProps", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "queueIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "allPaneTileIds", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: new Set()
        });
        Object.defineProperty(this, "isTargetedOverride", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "currentChunks", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "currentChunkIndex", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "currentUtterance", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "readGeneration", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "isSelectingProgrammatically", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        /** Timestamp of the last programmatic tile selection by readItem(). Used by
         *  external reactions (chat-thread.tsx) to ignore selection changes caused by
         *  Read Aloud rather than by user clicks. */
        Object.defineProperty(this, "lastProgrammaticSelectionTime", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: 0
        });
        Object.defineProperty(this, "disposers", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "synth", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: null
        });
        Object.defineProperty(this, "handleKeyDown", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (e) => {
                if (this.state === "idle")
                    return;
                if (e.ctrlKey || e.metaKey || e.altKey)
                    return;
                if (this.isEditableTarget(e.target))
                    return;
                if (e.key === " " || e.code === "Space") {
                    e.preventDefault();
                    if (this.state === "reading")
                        this.pause();
                    else if (this.state === "paused")
                        this.resume();
                }
                if (e.key === "Escape") {
                    e.preventDefault();
                    this.stop("user");
                }
            }
        });
        makeObservable(this);
        this.stores = stores;
        this.isSupported = typeof window !== "undefined" && "speechSynthesis" in window;
        if (this.isSupported) {
            this.synth = window.speechSynthesis;
            document.addEventListener("keydown", this.handleKeyDown);
        }
    }
    get currentTileId() {
        var _a, _b;
        return (_b = (_a = this.currentItem) === null || _a === void 0 ? void 0 : _a.associatedTileId) !== null && _b !== void 0 ? _b : null;
    }
    isReadingPane(pane) {
        return this.state !== "idle" && this.activePane === pane;
    }
    setPendingCommentId(commentId) {
        this.pendingCommentId = commentId;
    }
    dispose() {
        if (this.state !== "idle") {
            this.stop("user");
        }
        if (this.isSupported) {
            document.removeEventListener("keydown", this.handleKeyDown);
        }
    }
    start(pane, items, toolbarProps, allPaneTileIds, commentMode) {
        var _a, _b, _c;
        if (!this.isSupported)
            return;
        // Toggle off if already reading on the same pane
        if (this.isReadingPane(pane)) {
            this.stop("user");
            return;
        }
        // Stop reading on the other pane (global singleton)
        if (this.state !== "idle") {
            this.stop("pane-switch");
        }
        this.toolbarProps = toolbarProps;
        this.activePane = pane;
        this.state = "reading";
        this.queue = items;
        this.queueIndex = 0;
        this.allPaneTileIds = allPaneTileIds !== null && allPaneTileIds !== void 0 ? allPaneTileIds : new Set(items.filter(i => i.kind === "tile").map(i => i.associatedTileId));
        if (this.queue.length === 0) {
            this.stop("complete");
            return;
        }
        // If a comment was clicked before starting, jump directly to it.
        // skipTileSelect prevents readItem from selecting the associated tile so the
        // comment (not the tile) stays as the visual focus.
        let startIndex = 0;
        let skipTileSelect = false;
        if (this.pendingCommentId) {
            const pendingIdx = this.queue.findIndex(item => isCommentItem(item) && item.commentId === this.pendingCommentId);
            if (pendingIdx >= 0) {
                startIndex = pendingIdx;
                skipTileSelect = true;
            }
        }
        this.setupReactions();
        logToolbarEvent(LogEventName.TOOLBAR_READ_ALOUD_START, this.toolbarProps, {
            pane: this.activePane,
            documentId: (_a = this.toolbarProps.document) === null || _a === void 0 ? void 0 : _a.key,
            tileId: (_c = (_b = this.queue[startIndex]) === null || _b === void 0 ? void 0 : _b.associatedTileId) !== null && _c !== void 0 ? _c : null,
            trigger: "user",
            commentMode
        });
        this.queueIndex = startIndex;
        this.readItem(this.queue[startIndex], "auto", skipTileSelect);
    }
    stop(reason = "user") {
        var _a, _b;
        if (this.state === "idle")
            return;
        // Invalidate pending callbacks
        ++this.readGeneration;
        (_a = this.synth) === null || _a === void 0 ? void 0 : _a.cancel();
        this.disposeReactions();
        // Log before clearing state
        if (this.toolbarProps) {
            logToolbarEvent(LogEventName.TOOLBAR_READ_ALOUD_STOP, this.toolbarProps, {
                pane: this.activePane,
                documentId: (_b = this.toolbarProps.document) === null || _b === void 0 ? void 0 : _b.key,
                tileId: this.currentTileId,
                reason
            });
        }
        // Do NOT clear selectedTileIds — last tile remains selected.
        // Do NOT clear pendingCommentId — it persists so the next start() can jump
        // to the same comment. It is cleared when the user clicks a tile in the
        // workspace (via the selectedTileIds reaction in chat-thread.tsx).
        this.state = "idle";
        this.activePane = null;
        this.currentItem = null;
        this.toolbarProps = null;
        this.queue = [];
        this.queueIndex = 0;
        this.allPaneTileIds = new Set();
        this.isTargetedOverride = false;
        this.currentChunks = [];
        this.currentChunkIndex = 0;
        this.currentUtterance = null;
    }
    pause() {
        var _a;
        if (this.state === "reading") {
            (_a = this.synth) === null || _a === void 0 ? void 0 : _a.pause();
            this.state = "paused";
        }
    }
    resume() {
        var _a, _b;
        if (this.state === "paused") {
            this.state = "reading";
            if (!((_a = this.synth) === null || _a === void 0 ? void 0 : _a.speaking)) {
                // Paused between chunks — speak the next one
                this.speakCurrentChunk();
            }
            else {
                (_b = this.synth) === null || _b === void 0 ? void 0 : _b.resume();
            }
        }
    }
    replaceQueue(newItems, allPaneTileIds) {
        if (this.state === "idle" || this.isTargetedOverride)
            return;
        // Find current item in new queue by stable key
        const currentItem = this.currentItem;
        let newIndex = -1;
        if (currentItem) {
            if (currentItem.kind === "tile" && currentItem.associatedTileId) {
                newIndex = newItems.findIndex(item => item.kind === "tile" && item.associatedTileId === currentItem.associatedTileId);
            }
            else if (isCommentItem(currentItem)) {
                newIndex = newItems.findIndex(item => isCommentItem(item) && item.commentId === currentItem.commentId);
            }
            else if (currentItem.kind === "section-header") {
                newIndex = newItems.findIndex(item => item.kind === "section-header");
            }
        }
        this.queue = newItems;
        this.allPaneTileIds = allPaneTileIds !== null && allPaneTileIds !== void 0 ? allPaneTileIds : this.allPaneTileIds;
        if (newIndex >= 0) {
            // Current item still exists — update index, don't interrupt current speech
            // Invariant: currentItem may reference an item from a previous queue build
            // until the next readItem() call. This is intentional — it matches the
            // currently-speaking content. UI highlighting uses originTileId (stable key),
            // not object identity or speechText.
            this.queueIndex = newIndex;
        }
        else {
            // Current item was removed — set index so advanceToNextItem (which does
            // queueIndex + 1) picks up the item that now occupies the old position.
            if (newItems.length === 0) {
                this.stop("complete");
            }
            else {
                this.queueIndex = Math.max(-1, Math.min(this.queueIndex, newItems.length) - 1);
            }
        }
    }
    jumpToItem(index) {
        var _a;
        if (index < 0 || index >= this.queue.length || this.state === "idle")
            return;
        ++this.readGeneration;
        (_a = this.synth) === null || _a === void 0 ? void 0 : _a.cancel();
        this.queueIndex = index;
        // Note: always calls readItem (which speaks), even when paused. This is intentional —
        // jumpToItem is triggered by explicit user clicks (e.g., clicking a comment thread),
        // which signal "play this."
        this.readItem(this.queue[index], "user-click");
    }
    readItem(item, trigger = "auto", skipTileSelect = false) {
        var _a;
        this.currentItem = item;
        // Select associated tile, or clear selection for items without one.
        // skipTileSelect is true when starting from a pending comment — the user
        // clicked a comment (not a tile), so we preserve their deselected-tile state.
        if (!skipTileSelect) {
            this.isSelectingProgrammatically = true;
            this.lastProgrammaticSelectionTime = Date.now();
            this.stores.ui.setSelectedTileId((_a = item.associatedTileId) !== null && _a !== void 0 ? _a : '');
            this.isSelectingProgrammatically = false;
        }
        this.currentChunks = this.chunkText(item.speechText);
        this.currentChunkIndex = 0;
        // Log transition based on item kind
        this.logItemTransition(item, trigger);
        this.speakCurrentChunk();
    }
    logItemTransition(item, trigger) {
        var _a, _b, _c;
        if (!this.toolbarProps)
            return;
        if (isCommentItem(item)) {
            logToolbarEvent(LogEventName.TOOLBAR_READ_ALOUD_COMMENT_TRANSITION, this.toolbarProps, {
                pane: this.activePane,
                documentId: (_a = this.toolbarProps.document) === null || _a === void 0 ? void 0 : _a.key,
                tileId: item.originTileId || "document",
                commentId: item.commentId,
                threadIndex: item.threadIndex,
                commentIndex: item.commentIndex,
                trigger
            });
        }
        else if (item.kind === "tile") {
            logToolbarEvent(LogEventName.TOOLBAR_READ_ALOUD_TILE_TRANSITION, this.toolbarProps, {
                pane: this.activePane,
                documentId: (_b = this.toolbarProps.document) === null || _b === void 0 ? void 0 : _b.key,
                tileId: (_c = item.associatedTileId) !== null && _c !== void 0 ? _c : null,
                trigger
            });
        }
        // section-header items are not logged
    }
    speakCurrentChunk() {
        var _a;
        const gen = ++this.readGeneration;
        const chunk = this.currentChunks[this.currentChunkIndex];
        if (!chunk) {
            this.advanceToNextItem();
            return;
        }
        const utterance = new SpeechSynthesisUtterance(chunk);
        this.currentUtterance = utterance;
        utterance.onend = () => {
            if (gen !== this.readGeneration)
                return; // stale callback
            this.currentChunkIndex++;
            if (this.state === "paused")
                return; // let resume() handle it
            if (this.currentChunkIndex < this.currentChunks.length) {
                this.speakCurrentChunk();
            }
            else {
                this.advanceToNextItem();
            }
        };
        utterance.onerror = () => {
            if (gen !== this.readGeneration)
                return;
            this.stop("error");
        };
        (_a = this.synth) === null || _a === void 0 ? void 0 : _a.speak(utterance);
    }
    advanceToNextItem() {
        this.queueIndex++;
        if (this.queueIndex < this.queue.length) {
            this.readItem(this.queue[this.queueIndex]);
        }
        else {
            this.stop("complete");
        }
    }
    chunkText(text) {
        const sentencePattern = /[^.!?]+[.!?]+[\s]*/g;
        const sentences = [];
        let lastIndex = 0;
        let match;
        while ((match = sentencePattern.exec(text)) !== null) {
            sentences.push(match[0]);
            lastIndex = sentencePattern.lastIndex;
        }
        // Capture trailing text without sentence-ending punctuation
        if (lastIndex < text.length) {
            const trailing = text.slice(lastIndex).trim();
            if (trailing)
                sentences.push(trailing);
        }
        // If no sentences found, treat whole text as one chunk
        if (sentences.length === 0)
            sentences.push(text);
        const chunks = [];
        let current = "";
        for (const sentence of sentences) {
            if (current.length + sentence.length > kMaxChunkLength && current.length > 0) {
                chunks.push(current.trim());
                current = sentence;
            }
            else {
                current += sentence;
            }
        }
        if (current.trim()) {
            chunks.push(current.trim());
        }
        return chunks;
    }
    setupReactions() {
        const { ui, persistentUI } = this.stores;
        this.disposers.push(reaction(() => [...ui.selectedTileIds], (newSelectedIds) => {
            var _a;
            if (this.state === "idle")
                return;
            if (this.isSelectingProgrammatically)
                return;
            // Empty selection guard: if currently reading an item without associatedTileId,
            // readItem already handled the setSelectedTileId('') — don't stop.
            if (newSelectedIds.length === 0 || (newSelectedIds.length === 1 && newSelectedIds[0] === "")) {
                if (this.currentItem && !this.currentItem.associatedTileId) {
                    return;
                }
            }
            const selectedInThisPane = newSelectedIds.filter(id => this.allPaneTileIds.has(id));
            const selectedInOtherPane = newSelectedIds.length > 0 && selectedInThisPane.length === 0;
            if (selectedInOtherPane) {
                this.stop("pane-switch");
                return;
            }
            if (selectedInThisPane.length === 1 && selectedInThisPane[0] !== this.currentTileId) {
                const selectedId = selectedInThisPane[0];
                ++this.readGeneration;
                (_a = this.synth) === null || _a === void 0 ? void 0 : _a.cancel();
                if (this.currentItem && this.currentItem.kind !== "tile") {
                    // Currently reading non-tile items — workspace tile click means targeted mode
                    this.buildTargetedSubQueue(selectedId);
                }
                else {
                    // Same-pane tile switch (existing behavior — jump to tile, continue)
                    const tileIndex = this.queue.findIndex(item => item.associatedTileId === selectedId && item.kind === "tile");
                    if (tileIndex >= 0) {
                        this.queueIndex = tileIndex;
                        if (this.state === "paused") {
                            this.currentItem = this.queue[tileIndex];
                            this.currentChunks = this.chunkText(this.queue[tileIndex].speechText);
                            this.currentChunkIndex = 0;
                        }
                        else {
                            this.readItem(this.queue[tileIndex], "user-click");
                        }
                    }
                    else {
                        // Tile not in queue — stop
                        this.stop("user");
                    }
                }
            }
        }));
        // React to tab/section changes
        if (this.activePane === "right") {
            this.disposers.push(reaction(() => persistentUI.problemWorkspace.primaryDocumentKey, () => { this.stop("tab-switch"); }));
        }
        else {
            // Left pane: stop if the active tab or curriculum section changes
            this.disposers.push(reaction(() => persistentUI.activeNavTab, () => { this.stop("tab-switch"); }), reaction(() => persistentUI.currentDocumentGroupId, () => { this.stop("tab-switch"); }));
            // Left pane: stop if the chat panel is closed (immediate, not debounced)
            // Panel opened mid-read is handled by the reactive queue rebuild in ReadAloudButton.
            this.disposers.push(reaction(() => persistentUI.showChatPanel, (isOpen) => {
                if (!isOpen) {
                    this.stop("user");
                }
            }));
        }
    }
    buildTargetedSubQueue(tileId) {
        const tileItem = this.queue.find(item => item.kind === "tile" && item.associatedTileId === tileId);
        const commentItems = this.queue.filter(item => isCommentItem(item) && item.originTileId === tileId);
        const items = [];
        if (tileItem)
            items.push(tileItem);
        items.push(...commentItems);
        if (items.length === 0) {
            this.stop("user");
            return;
        }
        this.isTargetedOverride = true;
        this.queue = items;
        this.queueIndex = 0;
        if (this.state === "paused") {
            this.currentItem = items[0];
            this.currentChunks = this.chunkText(items[0].speechText);
            this.currentChunkIndex = 0;
        }
        else {
            this.readItem(items[0], "user-click");
        }
    }
    disposeReactions() {
        this.disposers.forEach(dispose => dispose());
        this.disposers = [];
    }
    isEditableTarget(target) {
        if (!(target instanceof HTMLElement))
            return false;
        const tagName = target.tagName.toLowerCase();
        if (tagName === "input" || tagName === "textarea")
            return true;
        if (target.isContentEditable)
            return true;
        const inInteractiveContainer = !!target.closest("[role='dialog'],[role='menu'],[role='listbox']");
        return inInteractiveContainer;
    }
}
__decorate([
    observable
], ReadAloudService.prototype, "state", void 0);
__decorate([
    observable
], ReadAloudService.prototype, "activePane", void 0);
__decorate([
    observable
], ReadAloudService.prototype, "currentItem", void 0);
__decorate([
    observable
], ReadAloudService.prototype, "queue", void 0);
__decorate([
    observable
], ReadAloudService.prototype, "pendingCommentId", void 0);
__decorate([
    computed
], ReadAloudService.prototype, "currentTileId", null);
__decorate([
    action
], ReadAloudService.prototype, "setPendingCommentId", null);
__decorate([
    action
], ReadAloudService.prototype, "start", null);
__decorate([
    action
], ReadAloudService.prototype, "stop", null);
__decorate([
    action
], ReadAloudService.prototype, "pause", null);
__decorate([
    action
], ReadAloudService.prototype, "resume", null);
__decorate([
    action
], ReadAloudService.prototype, "replaceQueue", null);
__decorate([
    action
], ReadAloudService.prototype, "jumpToItem", null);
__decorate([
    action
], ReadAloudService.prototype, "readItem", null);
let instance = null;
export function getReadAloudService(stores) {
    if (!instance) {
        instance = new ReadAloudService(stores);
    }
    return instance;
}
// Must be called in test teardown (afterEach) to avoid leaked keydown listeners.
export function resetReadAloudService() {
    if (instance) {
        instance.stop("user");
        instance.dispose();
    }
    instance = null;
}
//# sourceMappingURL=data:application/json;base64,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